import React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import UserMethods, { UserAvatarSize } from './user.local.methods'
import Icon from 'blocks.simple/icon/icon'
import Badge from 'blocks.simple/notifyBadge/notifyBadge'
import { authType, isActiveDirectory } from 'core/helpers/user'
import helpersCore from 'core/helpers'
import { cn } from 'ethcss'
import item from 'blocks.simple/item/item.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import styles from './user.jcss'
import { text as textStyles } from 'theme'
import translate from 'core/translate'
import { Typography } from 'atoms/Typography'
import { defaultThemeStyles } from 'blocks.simple/icon/icon.theme'
import { userActions } from './user.state'
import { isHorizontalMenu } from 'core/helpers/menu'
import DotTooltip from '../../blocks.simple/dotTooltip/dotTooltip'
import { IRole } from '../../core/models/Roles'
import { IUser } from 'core/models/Users'
import { IRootState } from 'core/store/rootReducer'

export interface IUserProps {
    id?: number
    isDeletable?: boolean
    type?: string | null
    email?: string | null
    role?: string | null
    roles?: IRole[]
    editableAvatar?: boolean
    photo?: string | null
    padding?: string
    switchAccount?: boolean
    active?: boolean
    multipleAccounts?: boolean
    containerClassName?: string | object
    authType?: string
    minimize?: boolean
    size?: UserAvatarSize
    iconColor?: string
    onCloseSubMenu?: () => void
}

interface IUserStateProps {
    mainUser: IUser | null
    current: IUser | null
}

interface IUserDispatchProps {
    toggleUserPanel: () => void
}

type IUserComponentProps = IUserProps & IUserStateProps & IUserDispatchProps

const User = (props: IUserComponentProps) => {
    const p_ = {
        ...props,
        active: props.active ?? false,
        authType: props.authType ?? authType.smartplayer,
        containerClassName: props.containerClassName ?? styles.wrapper,
        editableAvatar: props.editableAvatar ?? false,
        minimize: props.minimize ?? false,
        multipleAccounts: props.multipleAccounts ?? false,
        padding: props.padding ?? 'p_mini',
        size: props.size ?? 80,
    }
    const { getAvatarSizes } = UserMethods({ minimize: p_.minimize, size: p_.size })
    const horizontalMenu = isHorizontalMenu()
    const isMultipleAccountsExist = Boolean(p_.mainUser?.multipleAccounts.length)
    const getUserName = (user: IUser | null) => (user ? user.name || user.email : null)

    const avatarSize = getAvatarSizes()
    const avatarIconSize = getAvatarSizes(true)
    const iconSize = avatarIconSize ?? 16
    const isMainUserMinimize = isMultipleAccountsExist && p_.minimize
    const roles = p_.roles ? helpersCore.translateRoles(p_.roles) : undefined

    return horizontalMenu ? (
        <div onClick={p_.toggleUserPanel} className={styles.wrapper_x}>
            <div className={styles.userWrapper_x}>
                <DotTooltip tooltip={{ html: getUserName(p_.current) }} containerClass={item.ellipsis}>
                    <div className={item.ellipsis}>{getUserName(p_.current)}</div>
                </DotTooltip>
            </div>
        </div>
    ) : (
        <div className={cn(p_.containerClassName, grid[p_.padding], 'test-user-account')} onClick={p_.toggleUserPanel}>
            <div className={styles.imgWrapper} style={avatarSize}>
                {!p_.photo && (
                    <div>
                        <Icon
                            color={
                                p_.iconColor ||
                                `var(--var-module-ui-component-icon-style-mainSub, ${defaultThemeStyles.mainSub})`
                            }
                            type={isActiveDirectory(p_.authType) ? 'usersAD' : 'users'}
                            size={iconSize}
                            default={!p_.editableAvatar || isMainUserMinimize}
                        />
                    </div>
                )}
                {p_.photo && (
                    <div
                        className={styles.img}
                        style={{
                            backgroundImage: `url(${p_.photo})`,
                            cursor: p_.editableAvatar || isMainUserMinimize ? 'pointer' : 'default',
                        }}
                    />
                )}
                {p_.role && p_.mainUser && !!p_.mainUser.multipleAccounts.length && !p_.minimize && (
                    <div className={styles.badge}>
                        <Badge
                            type="active"
                            label={<span className={textStyles.up}>{p_.role.charAt(0)}</span>}
                            textSize="md"
                        />
                    </div>
                )}
                {p_.multipleAccounts && roles?.[0] && (
                    <div className={styles.badge}>
                        <Badge
                            type={p_.active ? 'active' : 'disable'}
                            label={<span className={textStyles.up}>{roles[0].label.charAt(0)}</span>}
                            textSize="md"
                        />
                    </div>
                )}
            </div>
            {!p_.multipleAccounts && (
                <div className={cn(styles.title, { [styles.hidden]: p_.minimize })}>
                    <div className={item.ellipsis}>{p_.email}</div>
                </div>
            )}
            {!p_.multipleAccounts && roles?.[0] && <div className={styles.role}>{roles[0].label}</div>}
            {p_.multipleAccounts && (
                <div className={styles.content}>
                    <div className={cn(grid.rowCenter, grid.noWrap)}>
                        <div className={item.ellipsisWrapper}>
                            <div className={item.ellipsis}>{p_.email}</div>
                        </div>
                    </div>
                    {roles?.[0] && (
                        <div className={cn(grid.rowCenter, grid.noWrap, textStyles.up, grid.mt_nano)}>
                            <div className={item.ellipsisWrapper}>
                                <div className={item.ellipsis}>{roles[0].label}</div>
                            </div>
                        </div>
                    )}
                    {p_.type && (
                        <div className={cn(grid.rowCenter, grid.noWrap, textStyles.mini, grid.mt_nano)}>
                            <div className={item.ellipsisWrapper}>
                                <Typography className={item.ellipsis}>{translate(p_.type)}</Typography>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const mapStateToProps = (state: IRootState): IUserStateProps => ({
    mainUser: state.user.mainUser,
    current: state.user.data,
})

const mapDispatchToProps = (dispatch: Dispatch): IUserDispatchProps => ({
    toggleUserPanel: () => dispatch(userActions.toggleUserPanel()),
})

export default connect<IUserStateProps, IUserDispatchProps, IUserProps, IRootState>(
    mapStateToProps,
    mapDispatchToProps
)(User)
